import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Mail, ShoppingBag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CtaBanner() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email,
        source: "cta_banner",
      });
      toast.success(data.message || "You're on the list.");
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cta"
      data-testid="cta-banner"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="relative overflow-hidden rounded-3xl border border-[#d4af37]/25 bg-gradient-to-br from-[#1a1408] via-[#0f0f0f] to-[#0a0a0a] p-10 md:p-16">
          {/* decorative */}
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-[#d4af37]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#d4af37]/5 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37]">
                Start your 30 days
              </p>
              <h2 className="font-serif-display mt-4 text-4xl sm:text-5xl text-white leading-[1.05] tracking-tight">
                The next 30 days <em className="gold-text">happen anyway.</em>
                <br />
                Use them.
              </h2>
              <p className="mt-5 text-neutral-300 max-w-md leading-relaxed">
                Get the book today, or join the list for chapter previews,
                free worksheets and launch-week bonuses.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  asChild
                  data-testid="cta-buy-now-btn"
                  className="bg-[#d4af37] text-black hover:bg-[#e5c158] rounded-full h-12 px-7 text-sm font-medium shadow-[0_0_50px_rgba(212,175,55,0.25)] transition-all hover:translate-y-[-1px]"
                >
                  <a
                    href="https://kcuttstudios.gumroad.com/l/ijgso"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    Buy now — $16.99
                  </a>
                </Button>
                <span className="text-xs text-neutral-500">
                  Instant PDF · EPUB · Lifetime updates
                </span>
              </div>
            </div>

            <form
              onSubmit={submit}
              data-testid="cta-subscribe-form"
              className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-7"
            >
              <label className="text-xs uppercase tracking-[0.25em] text-[#d4af37]/80 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" />
                Or join the list
              </label>
              <p className="mt-2 text-sm text-neutral-400">
                Free chapter + Day 1 worksheet. No spam — ever.
              </p>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@domain.com"
                  data-testid="cta-email-input"
                  className="bg-[#0f0f0f] border-white/10 text-white placeholder:text-neutral-600 h-12 rounded-full px-5 focus-visible:ring-[#d4af37] focus-visible:border-[#d4af37]/50"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="cta-subscribe-submit"
                  className="bg-white text-black hover:bg-[#e5c158] rounded-full h-12 px-6 text-sm font-medium whitespace-nowrap"
                >
                  {loading ? "Joining…" : "Get free chapter"}
                  {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>

              <p className="mt-4 text-[11px] text-neutral-500">
                By subscribing you agree to occasional emails. Unsubscribe any
                time.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
